import { Injectable } from '@angular/core';
import {
  StateService,
  TransitionService,
  UrlParts,
  UrlService,
} from '@uirouter/core';

import { LoadingService } from 'src/app/components/loading/loading.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { WindowLocationService } from 'src/app/shared/services/location-helper.service';
import { CanAccessAppsService } from './can-access-apps.service';
import { CustomAuthService } from './custom-auth.service';
import { OpenidConnectService } from './openid-connect.service';
import { SelectedCompanyUrlHandlerService } from './selected-company-url-handler.service';
import { UrlStateService } from './url-state.service';
import { UserAuthService } from './user-auth.service';
import { UserStateService } from './user-state.service';

const AUTH_STATE_PARAM_ROUTES = [
  'apps.auth.unauthorized',
  'apps.auth.unregistered',
  'apps.auth.createaccount',
];

@Injectable({
  providedIn: 'root',
})
export class UserstateBootstrapService {
  private initialized = false;

  constructor(
    private loadingService: LoadingService,
    private userAuthService: UserAuthService,
    private userStateService: UserStateService,
    private urlStateService: UrlStateService,
    private canAccessAppsService: CanAccessAppsService,
    private customAuthService: CustomAuthService,
    private openidConnectService: OpenidConnectService,
    private stateService: StateService,
    private transitionService: TransitionService,
    private urlService: UrlService,
    private broadcasterService: BroadcasterService,
    private windowLocationService: WindowLocationService,
    private selectedCompanyUrlHandlerService: SelectedCompanyUrlHandlerService,
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.registerUrlRules();
    this.startSilentAuth();
    this.registerAuthListeners();
    this.registerTransitionHooks();
    this.selectedCompanyUrlHandlerService.initialize();
  }

  private startSilentAuth(): void {
    this.loadingService.startGlobal('auth-buttons-silent');
    this.userAuthService
      .authenticate(false)
      .catch(() => undefined)
      .finally(() => {
        this.loadingService.stopGlobal('auth-buttons-silent');
      });
  }

  private registerAuthListeners(): void {
    this.broadcasterService.on('risevision.user.signedOut', () => {
      this.stateService.go('apps.auth.unauthorized').catch(() => undefined);
    });

    this.broadcasterService.on('risevision.user.authorized', () => {
      const currentState = this.stateService.current?.name || '';

      if (
        currentState.indexOf('apps.auth') !== -1 &&
        currentState !== 'apps.auth.unsubscribe'
      ) {
        if (
          currentState === 'apps.auth.unregistered' &&
          !this.userStateService.isRiseVisionUser()
        ) {
          return;
        }

        this.urlStateService.redirectToState(
          this.stateService.params['state'] as string,
        );
      }
    });
  }

  private registerTransitionHooks(): void {
    this.transitionService.onBefore(
      {
        to: (state) =>
          !!(
            state.data &&
            (state.data as { requiresAuth?: boolean }).requiresAuth
          ),
      },
      () => this.canAccessAppsService.canAccessApps(),
    );

    this.transitionService.onBefore(
      {
        to: (state) => AUTH_STATE_PARAM_ROUTES.includes(state.name),
      },
      (trans) => {
        const toParams = trans.params('to');
        const fromParams = trans.params('from');

        if (!toParams['state'] && fromParams['state']) {
          return this.stateService.target(trans.to().name, {
            ...this.stateService.params,
            state: fromParams['state'],
          });
        }
      },
    );
  }

  private registerUrlRules(): void {
    this.urlService.rules.rule(
      this.urlService.rules.urlRuleFactory.create(
        (url?: UrlParts) => !!(url?.hash && url.hash.match(/.*(jwt_token)=.*/)),
        () => this.handleSsoSuccess(),
      ),
    );

    this.urlService.rules.rule(
      this.urlService.rules.urlRuleFactory.create(
        (url?: UrlParts) =>
          !!(url?.hash && url.hash.match(/.*(error|error_description)=.*/)),
        () => this.handleSsoError(),
      ),
    );

    this.urlService.rules.when(/\/.*(id_token|access_token)=.*/, () =>
      this.handleGoogleAuthFromPath(),
    );

    this.urlService.rules.rule(
      this.urlService.rules.urlRuleFactory.create(
        (url?: UrlParts) =>
          !!(url?.hash && url.hash.match(/.*(id_token|access_token)=.*/)),
        () => this.handleGoogleAuthFromHash(),
      ),
    );

    this.urlService.rules.when(/\/resetpassword\/.+\/.+/, () =>
      this.handleResetPassword(),
    );
  }

  private handleSsoSuccess(): void {
    console.log('SSO result received');
    const location = this.windowLocationService.get();
    const tokens = location.hash.split('&');
    if (tokens.length < 2) {
      return;
    }
    const token1 = tokens[0].split('=')[1];
    const token2 = tokens[1].split('=')[1];
    const userInfo = {
      item: token1,
    };

    this.customAuthService
      ._updateToken(token2, userInfo)
      .then(() => {
        this.userAuthService.setLoginMethod('SSO');
        return this.userAuthService.authenticate(true);
      })
      .then(() => {
        location.hash = '';
      })
      .catch((e) => {
        return this.stateService
          .go('apps.auth.unauthorized', {
            authError: e,
          })
          .catch(() => undefined);
      });
  }

  private handleSsoError(): void {
    console.log('SSO result received');
    const location = this.windowLocationService.get();
    const tokens = location.hash.split('&');
    if (tokens.length < 2) {
      return;
    }
    const token1 = tokens[0].split('=')[1];
    const token2 = tokens[1].split('=')[1];

    this.stateService.go('apps.auth.unauthorized', {
      authError: {
        code: token1,
        message: decodeURIComponent(token2),
        type: 'sso_error',
      },
      replace: true,
    });
  }

  private handleGoogleAuthFromPath(): void {
    console.log('Google Auth result received 1');

    const location = this.windowLocationService.get();
    let href = location.href;
    href = href.replace('#/', '#');

    this.openidConnectService
      .signinRedirectCallback(href)
      .then(() => {
        location.hash = '';

        this.userAuthService.setLoginMethod('Google');
        return this.userAuthService.authenticate(true);
      })
      .catch(() => {
        location.hash = '';
      });
  }

  private handleGoogleAuthFromHash(): void {
    console.log('Google Auth result received 2');
    const location = this.windowLocationService.get();

    this.openidConnectService
      .signinRedirectCallback()
      .then(() => {
        this.userAuthService.setLoginMethod('Google');
        return this.userAuthService.authenticate(true);
      })
      .then(() => {
        location.hash = '';
      })
      .catch((e) => {
        return this.stateService
          .go('apps.auth.unauthorized', {
            authError: e,
          })
          .catch(() => undefined);
      });
  }

  private handleResetPassword(): void {
    const parts = this.windowLocationService.get().href.split('/');
    const token = parts[parts.length - 1];
    const user = parts[parts.length - 2];

    this.stateService.go('apps.auth.resetpassword', {
      user,
      token,
    });
  }
}
